import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap, take, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { TokenService } from '../services/token.service';

const RETRY_HEADER = 'X-Auth-Retry';

/**
 * Interceptor funcional de autenticación (Angular 17).
 *
 * Antes de cada petición:
 *  - Si hay token válido (>10 min hasta expiración) → lo inyecta y envía.
 *  - Si el token expira en <10 min o ya expiró → refresca primero, luego envía.
 *  - Si no hay token → lo obtiene primero, luego envía.
 *
 * Ante un 401 inesperado (token rechazado por el servidor):
 *  - Fuerza un refresco y reintenta la petición una única vez.
 *  - Si el reintento también falla, propaga el error.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // No interceptar el endpoint de obtención de token
  if (req.url.includes('/token')) {
    return next(req);
  }

  const tokenService = inject(TokenService);

  return tokenService.initializeToken().pipe(
    take(1),
    switchMap(() => {
      const authReq = req.clone({
        setHeaders: { Authorization: `Bearer ${tokenService.getToken()}` },
      });

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // 401 sin reintento previo: forzar refresco y reintentar una sola vez
          if (error.status === 401 && !req.headers.has(RETRY_HEADER)) {
            return tokenService.fetchToken().pipe(
              take(1),
              switchMap(() => {
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${tokenService.getToken()}` },
                  headers: req.headers.set(RETRY_HEADER, 'true'),
                });
                return next(retryReq);
              }),
            );
          }
          return throwError(() => error);
        }),
      );
    }),
  );
};
